<template>
  <figure data-testid="schedule-week-preview">
    <div class="space-y-1">
      <div
        v-for="(day, index) in DAYS_OF_WEEK"
        :key="day"
        class="flex items-center gap-2"
        data-testid="schedule-week-row"
      >
        <span
          class="w-8 shrink-0 text-xs font-medium"
          :class="
            selected.has(day) ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'
          "
          >{{ day }}</span
        >
        <div class="relative h-4 flex-1 overflow-hidden rounded-sm bg-gray-100 dark:bg-white/5">
          <span
            v-for="hour in [6, 12, 18]"
            :key="hour"
            class="absolute inset-y-0 w-px bg-gray-200 dark:bg-white/10"
            :style="{ left: `${(hour / 24) * 100}%` }"
            aria-hidden="true"
          />
          <span
            v-for="(segment, i) in segmentsByDay[index]"
            :key="i"
            class="absolute inset-y-0 rounded-sm"
            :class="enabled ? 'bg-indigo-500 dark:bg-indigo-400' : 'bg-gray-400 dark:bg-gray-500'"
            :style="{
              left: `${(segment.from / 1440) * 100}%`,
              width: `${((segment.to - segment.from) / 1440) * 100}%`,
            }"
            data-testid="schedule-week-segment"
          />
        </div>
      </div>
    </div>
    <div
      class="mt-1 ml-10 flex justify-between text-[10px] text-gray-400 dark:text-gray-500"
      aria-hidden="true"
    >
      <span>00</span><span>06</span><span>12</span><span>18</span><span>24</span>
    </div>
    <figcaption
      class="mt-2 text-xs text-gray-500 dark:text-gray-400"
      data-testid="schedule-week-caption"
    >
      {{ caption }}
    </figcaption>
  </figure>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { DAYS_OF_WEEK, type DayOfWeek } from '@/api/schedules';
import { type DaySegment, formatDuration, weeklySegments } from '@/lib/schedule';

const props = defineProps<{
  days: DayOfWeek[];
  /** Null while the times are invalid; the rows then stay empty. */
  startMinute: number | null;
  length: number | null;
  zoneId: string;
  enabled: boolean;
}>();

const selected = computed(() => new Set(props.days));

const segments = computed(() =>
  props.startMinute != null && props.length != null && props.length > 0
    ? weeklySegments(props.days, props.startMinute, props.length)
    : []
);

const segmentsByDay = computed(() => {
  const rows: DaySegment[][] = DAYS_OF_WEEK.map(() => []);
  for (const segment of segments.value) rows[segment.day]!.push(segment);
  return rows;
});

const caption = computed(() => {
  if (segments.value.length === 0) return 'Pick days and a valid time window to see the week.';
  const total = props.days.length * props.length!;
  const running = `Typical week in ${props.zoneId}: runs up to ${formatDuration(total)}. Daylight saving changes can shift a day's window.`;
  return props.enabled ? running : `${running} Disabled: nothing happens until you enable it.`;
});
</script>
